import { useEffect, useState } from 'react';
import { DeviceEventEmitter, NativeModules, Platform } from 'react-native';

export type KeyboardInsetState = { height: number; visible: boolean };

const { TonyteKeyboardInsets } = NativeModules as {
  TonyteKeyboardInsets?: { start: () => Promise<void> };
};

const INITIAL_STATE: KeyboardInsetState = { height: 0, visible: false };

/**
 * Android-only, real WindowInsets(IME)-backed keyboard height — see
 * TonyteKeyboardInsetsModule.kt for why RN's own JS `Keyboard` module
 * isn't sufficient on its own: it does not re-fire when the IME's height
 * changes without a full hide/show cycle, so switching Gboard's QWERTY
 * keyboard to its (taller) emoji picker never reaches JS, and anything
 * relying only on `Keyboard.addListener('keyboardDidShow', ...)` — e.g.
 * `KeyboardAvoidingView` on Android — keeps using the stale, shorter
 * height. This subscribes to the actual OS-level inset instead, which
 * fires on every genuine IME height change regardless of cause.
 *
 * Returns `{ height: 0, visible: false }` on iOS or if the native module
 * is unavailable for any reason — callers should treat that as "nothing
 * to do here" (iOS already has a working, real mechanism via
 * KeyboardAvoidingView's `padding` behavior), never as an error.
 */
export function useKeyboardInsets(): KeyboardInsetState {
  const [state, setState] = useState<KeyboardInsetState>(INITIAL_STATE);

  useEffect(() => {
    if (Platform.OS !== 'android' || !TonyteKeyboardInsets) return undefined;
    let cancelled = false;
    TonyteKeyboardInsets.start().catch(() => {
      // Best-effort — if attaching the native listener fails (e.g. no
      // current activity yet at this exact moment), the composer simply
      // falls back to whatever KeyboardAvoidingView/layout already does;
      // no crash, no dead UI.
    });
    const subscription = DeviceEventEmitter.addListener(
      'TonyteKeyboardInsetChanged',
      (event: { height: number; visible: boolean }) => {
        if (!cancelled) setState({ height: event.height, visible: event.visible });
      },
    );
    return () => {
      cancelled = true;
      subscription.remove();
    };
  }, []);

  return state;
}
